'use strict';

import initCollapsibleSpecs from 'Components/collapsibleSpecs';

/**
 * @class VZDrawer
 */
export default class VZDrawer extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.scrollBarWidth = window.innerWidth - document.body.clientWidth;
        this.pageHeader = document.querySelector('.js-page-header');
        this.isMinicart = this.classList.contains('minicart-drawer');
        this.shouldManageScrollbarPadding = !this.isMinicart;

        document.addEventListener('hidden.bs.offcanvas', this.removeDrawer.bind(this));
        document.addEventListener('show.bs.offcanvas', this.prepareDocumentBody.bind(this));
        document.addEventListener('hide.bs.offcanvas', this.constructor.preventDrawerClosing.bind(this));
        document.addEventListener('show.bs.modal', this.handleModalFocusTrap.bind(this));
        initCollapsibleSpecs();
    }

    disconnectedCallback() {
        document.removeEventListener('hidden.bs.offcanvas', this.removeDrawer);
        document.removeEventListener('show.bs.offcanvas', this.prepareDocumentBody);
        document.removeEventListener('hide.bs.offcanvas', this.constructor.preventDrawerClosing);
        document.removeEventListener('show.bs.modal', this.handleModalFocusTrap);
    }

    /**
     * @function handleModalFocusTrap
     * @description When a drawer is open, if a modal is opened thereafter, conflicting focus traps cause an infinite loop.
     * We deactivate the drawer's focustrap when the modal opens, and reactivate it when the modal closes.
     * @param {Event} event - The show.bs.modal event.
     */
    handleModalFocusTrap(event) {
        if (!this.classList.contains('show')) return;

        /* eslint-disable no-underscore-dangle */
        // Get drawer instance and deactivate its focustrap
        const Offcanvas = window.bs?.Offcanvas || window.bootstrap?.Offcanvas;
        const drawerInstance = Offcanvas?.getInstance(this);

        if (drawerInstance && drawerInstance._focustrap) {
            drawerInstance._focustrap.deactivate();

            // Reactivate drawer's focustrap when modal is hidden
            const modalElement = event.target;
            modalElement.addEventListener(
                'hidden.bs.modal',
                () => {
                    // Only reactivate if drawer is still open
                    if (this.classList.contains('show') && drawerInstance._focustrap) {
                        drawerInstance._focustrap.activate();
                    }
                },
                { once: true }
            );
        }
        /* eslint-enable no-underscore-dangle */
    }

    /**
     * @function preventDrawerClosing
     * @description When a drawer is open, and then a modal is opened, this function prevents the drawer from closing when
     * the modal is interacted with.
     * @param {Event} event - The hide.bs.offcanvas event.
     */
    static preventDrawerClosing(event) {
        if (document.body.classList.contains('modal-open')) {
            event.preventDefault();
        }
    }

    /**
     * @function removeDrawer
     * @description removes the drawer from the DOM and restores focus to the trigger element
     * @param {Event} event - The hidden.bs.offcanvas event
     */
    removeDrawer(event) {
        // Only handle events for this specific drawer element
        if (event.target !== this) return;

        // BO-3270: Cleanup manual focus trap if it exists
        // eslint-disable-next-line no-underscore-dangle
        const cleanupFocusTrap = this._cleanupFocusTrap;
        if (cleanupFocusTrap && typeof cleanupFocusTrap === 'function') {
            cleanupFocusTrap();
        }

        // Restore focus to the element that triggered the drawer
        // eslint-disable-next-line no-underscore-dangle
        const triggerElement = this._triggerElement;
        if (triggerElement && typeof triggerElement.focus === 'function') {
            setTimeout(() => triggerElement.focus(), 0);
        }

        // Dispatch minicart close event if this is a minicart drawer
        if (this.isMinicart) {
            document.dispatchEvent(new CustomEvent('minicart:close'));
        }

        if (this.shouldManageScrollbarPadding) {
            document.body.style.paddingRight = 0;
            this.pageHeader.style.paddingRight = 0;
        }

        const parentElement = this.parentElement;
        parentElement.remove();
    }

    /**
     * @function prepareDocumentBody
     * @description Sets some styling on the body to match what the drawer will set from Bootstrap.
     * This is to make sure there is no "jumpy" effect for some properties that are missing initially. (boo Bootstrap)
     * We only need to do this once, as Bootstrap will set the proper styling after the drawer has already been opened once.
     * See BO-1532 for more context.
     * @param {Event} event - The show.bs.offcanvas event
     */
    prepareDocumentBody(event) {
        // Only handle events for this specific drawer element
        if (event.target !== this) return;

        if (!('drawerInitiated' in document.body.dataset)) {
            document.body.dataset.drawerInitiated = '';
            if (this.shouldManageScrollbarPadding) {
                document.body.style.paddingRight = `${this.scrollBarWidth}px`;
                document.body.setAttribute('overflow', 'hidden');
            }
        }
        if (this.shouldManageScrollbarPadding) {
            this.pageHeader.style.paddingRight = `${this.scrollBarWidth}px`;
        }
    }
}

if (!window.customElements.get('vz-drawer')) {
    window.customElements.define('vz-drawer', VZDrawer);
}
